import type { Ref } from 'react'
import Link from 'next/link'
import { Image } from '@educator/components/Image'
import { getBasePath } from '@educator/config/basePath'
import { useTranslation } from '@mc/i18n'
import { TrackClick } from '../../../../tracking/educatorTracking'
import type { SubjectDetailItem } from '../../../../types/subjects'

interface CtaBoxProps {
  ctaHref: string
  ctaLabel: string
  promoText: string
  detailPairs: SubjectDetailItem[][]
  partnerLogoAlt: string
  ctaButtonRef?: Ref<HTMLDivElement>
}

const Divider = () => <div className='h-px w-full bg-white/20' />

const DetailRow = ({ items }: { items: SubjectDetailItem[] }) => (
  <div className='flex items-start justify-between py-3 text-white'>
    {items.map((item) => (
      <div key={item.label} className='flex flex-1 flex-col gap-2'>
        <span className='text-label-3 uppercase leading-label-3 tracking-[0.9px]'>
          {item.label}
        </span>
        <span className='text-body-3 leading-body-3'>{item.value}</span>
      </div>
    ))}
  </div>
)

export const CtaBox = ({
  ctaHref,
  ctaLabel,
  promoText,
  detailPairs,
  partnerLogoAlt,
  ctaButtonRef,
}: CtaBoxProps) => {
  const { t } = useTranslation('@mc/educator')
  const basePath = getBasePath()

  return (
    <div className='flex flex-col gap-4 bg-edu-accent-indigo-medium p-6 md:hidden'>
      <p className='max-w-[275px] m-0 text-body-3 leading-[20px] tracking-[0.28px] text-white'>
        {promoText}
      </p>
      <div ref={ctaButtonRef}>
        <TrackClick
          event='educatorPurchaseClick'
          cta_location='hero'
          cta={ctaLabel}
        >
          <TrackClick
            event='educatorSignClick'
            action={ctaLabel}
            location='hero'
          >
            <Link
              href={ctaHref}
              className='flex w-full items-center justify-center whitespace-nowrap bg-edu-accent-blue-muted p-3 text-center text-body-1 font-medium leading-body-1 text-edu-accent-indigo-medium'
            >
              {ctaLabel}
            </Link>
          </TrackClick>
        </TrackClick>
      </div>
      <div className='flex flex-col'>
        {detailPairs.map((pair, index) => (
          <div key={pair[0].label}>
            {index > 0 && <Divider />}
            <DetailRow items={pair} />
          </div>
        ))}
        <Divider />
        <div className='flex items-start justify-between py-1.5'>
          <div className='flex flex-1 flex-col justify-center'>
            <span className='text-label-3 uppercase leading-label-3 tracking-[0.9px] text-white'>
              {t('enrollment.hero.partnerLabel')}
            </span>
          </div>
          <div className='flex flex-1 items-start'>
            <Image
              src={`${basePath}/images/chicago-booth-white.svg`}
              alt={partnerLogoAlt}
              width={122}
              height={28}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
